/**
 * ReportCardsTab
 * ==============
 * Bulk report-card printing, one class + section at a time (printing the
 * whole school at once could be thousands of pages and freeze the UI).
 */

import React, { useMemo, useState, useRef } from 'react';
import { fetchSchoolInfo, gradeForPercentage, fetchCoScholastic, fetchSignatures } from './resultsApi';
import { printReportCards } from './reportCard';

const ReportCardsTab = ({ data, grades = [], schoolId }) => {
  const { subjects = [], students = [], exam } = data || {};
  const [classSel, setClassSel] = useState('');
  const [sectionSel, setSectionSel] = useState('');
  const [printing, setPrinting] = useState(false);
  const schoolRef = useRef(null);

  const classes = useMemo(
    () => [...new Set(students.map((s) => s.Class).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [students]
  );

  const sections = useMemo(
    () => [...new Set(
      students.filter((s) => s.Class === classSel).map((s) => s.Section).filter(Boolean)
    )].sort(),
    [students, classSel]
  );

  const selected = useMemo(() => {
    if (!classSel) return [];
    return students
      .filter((s) => s.Class === classSel && (!sections.length || s.Section === sectionSel))
      .sort((a, b) => String(a.Admission_Number || '').localeCompare(
        String(b.Admission_Number || ''), undefined, { numeric: true },
      ));
  }, [students, classSel, sectionSel, sections]);

  const ready = Boolean(classSel && (!sections.length || sectionSel));

  const handlePrint = async () => {
    if (!selected.length) return;
    setPrinting(true);
    try {
      if (!schoolRef.current) {
        try { schoolRef.current = await fetchSchoolInfo(schoolId); } catch { /* header optional */ }
      }
      const [areaRes, sigRes] = await Promise.allSettled([
        fetchCoScholastic(schoolId),
        fetchSignatures(schoolId),
      ]);
      const extras = {
        coScholastic: areaRes.status === 'fulfilled' ? (areaRes.value.areas || []) : [],
        signatures: sigRes.status === 'fulfilled' ? (sigRes.value.signatures || []) : [],
      };
      await printReportCards(selected, exam, subjects, grades, schoolRef.current, extras);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="cards-tab">
      <p className="grade-settings-help">
        Print report cards for a whole class at once. Select a class and section,
        then print — one card per student, one student per page.
      </p>

      <div className="results-toolbar">
        <select
          className="results-input results-filter"
          value={classSel}
          onChange={(e) => { setClassSel(e.target.value); setSectionSel(''); }}
        >
          <option value="">Select class...</option>
          {classes.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        {classSel && sections.length > 0 && (
          <select
            className="results-input results-filter"
            value={sectionSel}
            onChange={(e) => setSectionSel(e.target.value)}
          >
            <option value="">Select section...</option>
            {sections.map((sec) => (
              <option key={sec} value={sec}>{sec}</option>
            ))}
          </select>
        )}

        {ready && (
          <span className="results-count-pill">
            {selected.length} student{selected.length !== 1 ? 's' : ''}
          </span>
        )}

        <button
          type="button"
          className="results-export-btn"
          onClick={handlePrint}
          disabled={!ready || !selected.length || printing}
        >
          {printing ? 'Preparing...' : 'Print Report Cards'}
        </button>
      </div>

      {!ready ? (
        <div className="results-empty">
          <p>Select a class{sections.length ? ' and section' : ''} to continue</p>
        </div>
      ) : selected.length === 0 ? (
        <div className="results-empty">
          <p>No students found for this class</p>
        </div>
      ) : (
        <div className="results-table-wrap cards-preview">
          <table className="results-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Adm No</th>
                <th>Student</th>
                <th>Total</th>
                <th>%</th>
                <th>Grade</th>
              </tr>
            </thead>
            <tbody>
              {selected.map((s, i) => (
                <tr key={s.Student_Id}>
                  <td>{i + 1}</td>
                  <td>{s.Admission_Number || '—'}</td>
                  <td className="results-student-name">{s.Student_Name || '—'}</td>
                  <td>{s.obtained}<span className="results-mark-full">/{s.fullMarks}</span></td>
                  <td>{s.percentage}%</td>
                  <td className="results-grade">{gradeForPercentage(s.percentage, grades)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportCardsTab;
